import { apiClient } from './api-client';

export interface ApiUser {
  id: string;
  username: string;
  fullName: string;
  email: string;
  phone: string;
  roleId: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'DELETED' | 'INACTIVE' | 'active' | 'suspended';
  lastLogin: string;
  createdAt: string;
}

export interface ApiRole {
  id: string;
  name: string;
  description: string;
  isSystem: boolean;
  permissions: ApiPermission[];
}

export interface ApiPermission {
  id: string;
  name: string;
  description: string;
  resource: string;
  action: string;
}

export const usersService = {
  /**
   * List system users with optional filters
   * GET /api/users
   */
  list: async (params?: { search?: string; role?: string; status?: string }): Promise<{ data: ApiUser[] }> => {
    const res = await apiClient.get<any>('/users', params);
    const raw = res.data;
    const items: ApiUser[] = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.items)
      ? raw.items
      : [];
    return { data: items };
  },

  /**
   * Create a new system user
   * POST /api/users
   */
  create: async (data: {
    username: string;
    fullName?: string;
    displayName?: string;
    display_name?: string;
    email?: string;
    phone?: string;
    password?: string;
    roleId?: string;
    role_id?: string;
    status?: 'active' | 'suspended' | 'ACTIVE' | 'SUSPENDED';
  }): Promise<{ data: ApiUser }> => {
    const payload = {
      username: data.username.trim(),
      display_name: (data.display_name || data.fullName || data.displayName || data.username).trim(),
      fullName: (data.display_name || data.fullName || data.displayName || data.username).trim(),
      email: data.email?.trim() || '',
      phone: data.phone?.trim() || '',
      role_id: data.role_id || data.roleId || 'support_agent',
      roleId: data.role_id || data.roleId || 'support_agent',
      status: (data.status || 'ACTIVE').toUpperCase(),
      password: data.password,
    };
    const res = await apiClient.post<any>('/users', payload);
    const result = (res.data as any)?.data || res.data;
    return { data: result };
  },

  /**
   * Update system user details
   * PUT /api/users/:id
   */
  update: async (
    id: string,
    data: {
      username?: string;
      fullName?: string;
      displayName?: string;
      display_name?: string;
      email?: string;
      phone?: string;
      roleId?: string;
      role_id?: string;
      status?: 'active' | 'suspended' | 'ACTIVE' | 'SUSPENDED';
      password?: string;
    },
  ): Promise<{ data: ApiUser }> => {
    const payload = {
      ...(data.username ? { username: data.username.trim() } : {}),
      ...(data.display_name || data.fullName || data.displayName
        ? {
            display_name: (data.display_name || data.fullName || data.displayName)!.trim(),
            fullName: (data.display_name || data.fullName || data.displayName)!.trim(),
          }
        : {}),
      ...(data.email !== undefined ? { email: data.email.trim() } : {}),
      ...(data.phone !== undefined ? { phone: data.phone.trim() } : {}),
      ...(data.role_id || data.roleId ? { role_id: data.role_id || data.roleId, roleId: data.role_id || data.roleId } : {}),
      ...(data.status ? { status: data.status.toUpperCase() } : {}),
      ...(data.password ? { password: data.password } : {}),
    };
    const res = await apiClient.put<any>(`/users/${id}`, payload);
    const result = (res.data as any)?.data || res.data;
    return { data: result };
  },

  /**
   * Delete a system user
   * DELETE /api/users/:id
   */
  delete: async (id: string): Promise<{ data: { message: string } }> => {
    const res = await apiClient.delete<any>(`/users/${id}`);
    return { data: res.data };
  },

  /**
   * List all system roles and their permissions
   * GET /api/roles
   */
  listRoles: async (): Promise<{ data: ApiRole[] }> => {
    const res = await apiClient.get<any>('/roles');
    const raw = res.data;
    const items: ApiRole[] = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.items)
      ? raw.items
      : [];
    return { data: items };
  },

  /**
   * Update permissions for a role
   * PUT /api/roles/:id/permissions
   */
  updateRolePermissions: async (roleId: string, permissions: string[]): Promise<{ data: ApiRole }> => {
    const res = await apiClient.put<any>(`/roles/${roleId}/permissions`, { permissions });
    const result = (res.data as any)?.data || res.data;
    return { data: result };
  },

  /**
   * List all available permissions in the system
   * GET /api/permissions
   */
  listPermissions: async (): Promise<{ data: ApiPermission[] }> => {
    const res = await apiClient.get<any>('/permissions');
    const raw = res.data;
    const items: ApiPermission[] = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.items)
      ? raw.items
      : [];
    return { data: items };
  },
};
export default usersService;
